import { ComponentType, FC } from 'react';

interface IconPillProps {
  /** Any icon component that accepts a `className` prop (lucide-react, react-icons, etc.) */
  icon: ComponentType<{ className?: string }>;
  /** Visual size — controls padding + icon size. Default: 'md' */
  size?: 'sm' | 'md' | 'lg';
  /** Color tone. 'amber' = the Remi-branded amber tint (default). 'muted' = neutral gray. */
  tone?: 'amber' | 'muted';
  /** Extra classes appended to the pill container */
  className?: string;
}

const SIZE_CLASSES = {
  sm: { box: 'p-1', icon: 'w-3 h-3' },
  md: { box: 'p-1.5', icon: 'w-3.5 h-3.5' },
  lg: { box: 'p-2', icon: 'w-4 h-4' },
} as const;

const TONE_CLASSES = {
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-500/15 group-hover:bg-amber-200 dark:group-hover:bg-amber-500/25',
    icon: 'text-amber-700 dark:text-amber-300',
  },
  muted: {
    bg: 'bg-muted dark:bg-foreground/10 group-hover:bg-foreground/15',
    icon: 'text-muted-foreground',
  },
} as const;

/**
 * Tinted icon pill used to flag section headers, drawer items, and setting
 * cards. Two tones supported: `amber` (the Remi-branded gradient family) and
 * `muted` (neutral gray for non-Remi sections).
 *
 * The `group-hover:` variants assume the parent button/anchor uses `group`
 * — they intensify the tint on hover without requiring callers to set them.
 */
export const IconPill: FC<IconPillProps> = ({
  icon: Icon,
  size = 'md',
  tone = 'amber',
  className = '',
}) => {
  const s = SIZE_CLASSES[size];
  const t = TONE_CLASSES[tone];
  return (
    <div className={`${s.box} ${t.bg} rounded-lg transition-colors ${className}`}>
      <Icon className={`${s.icon} ${t.icon}`} />
    </div>
  );
};

export default IconPill;
